import { Link } from 'react-router-dom'
import type { UserEventDashboard } from '@fellowship/shared'
import { EYEBROW_STYLE } from '../format'
import { RsvpRow } from '../RsvpRow'
import { HostedEventRow } from '../HostedEventRow'

function Eyebrow({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ ...EYEBROW_STYLE, marginBottom: 12, ...style }}>{children}</div>
}

function EmptyCard({ title, body, cta }: { title: string; body?: string; cta: { to: string; label: string } }) {
  return (
    <div
      style={{
        padding: 28,
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 16,
        textAlign: 'center',
      }}
    >
      <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{title}</p>
      {body && <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>{body}</p>}
      <Link to={cta.to} style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
        {cta.label} →
      </Link>
    </div>
  )
}

export function MyEventsTab({ dashboard }: { dashboard: UserEventDashboard }) {
  const { upcoming, past, hostedEvents } = dashboard
  const drafts = hostedEvents.filter((e) => e.status === 'draft')
  const published = hostedEvents.filter((e) => e.status !== 'draft')
  const upcomingHosted = published.filter((e) => e.nextOccurrenceStartsAt !== null)
  const pastHosted = published.filter((e) => e.nextOccurrenceStartsAt === null)

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Upcoming */}
      <Eyebrow>Upcoming · {upcoming.length}</Eyebrow>
      {upcoming.length === 0 ? (
        <EmptyCard title="No upcoming RSVPs." cta={{ to: '/search', label: 'Find events nearby' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {upcoming.map((entry) => (
            <RsvpRow key={entry.rsvp.id} entry={entry} />
          ))}
        </div>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <>
          <Eyebrow style={{ marginTop: 32 }}>Drafts · {drafts.length}</Eyebrow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {drafts.map((summary) => (
              <HostedEventRow key={summary.id} summary={summary} />
            ))}
          </div>
        </>
      )}

      {/* Hosting */}
      <Eyebrow style={{ marginTop: 32 }}>Hosting · {upcomingHosted.length}</Eyebrow>
      {published.length === 0 ? (
        drafts.length > 0 ? (
          <div style={{ padding: '4px 0', fontSize: 13, color: 'var(--muted)' }}>
            No published events yet — finish a draft above to go live.
          </div>
        ) : (
          <EmptyCard
            title="No hosted events yet."
            body="Host your own potluck, hike, study, or social."
            cta={{ to: '/events/new', label: 'Host your first event' }}
          />
        )
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcomingHosted.map((summary) => (
              <HostedEventRow key={summary.id} summary={summary} />
            ))}
          </div>
          {upcomingHosted.length === 0 && (
            <div style={{ padding: '4px 0', fontSize: 13, color: 'var(--muted)' }}>No upcoming hosted events.</div>
          )}
          {pastHosted.length > 0 && (
            <>
              <Eyebrow style={{ marginTop: 24 }}>Past hosting · {pastHosted.length}</Eyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.65 }}>
                {pastHosted.map((summary) => (
                  <HostedEventRow key={summary.id} summary={summary} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Past RSVPs */}
      {past.length > 0 && (
        <>
          <Eyebrow style={{ marginTop: 32 }}>Past</Eyebrow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {past.map((entry) => (
              <RsvpRow key={entry.rsvp.id} entry={entry} past />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
